/**
 * The Plant app itself, floating over any screen of the ERP (EPIC 29 / P11; owner 2026-09-29: "make the side toggle
 * that will open the … application floating on screen").
 *
 * A round button on the right side of the Plant section's screens (and only there: the owner, 2026-09-29) opens a phone-sized window with the Plant app's own Flutter web build, served by the ERP at
 * /plant-app/ (copied in from the plant repository; git-ignored). The window is dragged by its title bar and closed with
 * ×; once opened, the app stays loaded while hidden, so it reopens at once and keeps what was on its screen.
 *   · SAMPLE — the app's own frozen sample day, in memory, with no sign-in: nothing is saved anywhere.
 *   · LIVE   — the real app at the chosen plant: sign in as on a phone; every entry is a real record in the ERP.
 * The app tells the ERP when it saved something ({type: 'saved'}); the ERP's plant figures are read again at once.
 * Any screen may open it: window.dispatchEvent(new CustomEvent('rtcom:open-plant-app')).
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { GripHorizontal, Radio, Smartphone, TestTube2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { cx } from '../../ui/primitives';

const APP_PATH = '/plant-app/';
type Mode = 'sample' | 'live';
interface Plant { code: string; name: string; source_record_id: string | null }

export const openPlantApp = () => window.dispatchEvent(new CustomEvent('rtcom:open-plant-app'));

export function PlantAppLauncher() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const allowed = can('plant.view');
  // Only in the Plant section; elsewhere the button and the window hide, and the app stays loaded for the way back.
  const inPlant = useLocation().pathname.startsWith('/plant');
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [mode, setMode] = useState<Mode>('sample');
  const [site, setSite] = useState('');
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  const plants = useQuery({
    queryKey: ['plant-app-launcher-plants'], enabled: allowed && mounted,
    queryFn: async () => {
      const { data, error } = await supabase.from('locations').select('code,name,source_record_id').eq('kind', 'PLANT').eq('status', 'ACTIVE').order('code');
      if (error) throw error;
      return data as Plant[];
    },
  });
  useEffect(() => { if (!site && plants.data?.[0]) setSite(plants.data[0].source_record_id ?? plants.data[0].code); }, [plants.data, site]);

  const installed = useQuery({
    queryKey: ['plant-app-installed'], enabled: allowed && mounted, staleTime: 60_000,
    queryFn: async () => {
      const res = await fetch(`${APP_PATH}index.html`, { cache: 'no-store' });
      return res.ok && (await res.text()).includes('flutter');
    },
  });

  useEffect(() => {
    const show = () => { setMounted(true); setOpen(true); };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; kind?: string; recordId?: string } | null;
      if (data?.type !== 'saved') return;
      setLastSaved(`${data.kind ?? 'record'} ${data.recordId ?? ''}`.trim());
      void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? '').startsWith('plant') });
    };
    window.addEventListener('rtcom:open-plant-app', show);
    window.addEventListener('message', onMessage);
    return () => { window.removeEventListener('rtcom:open-plant-app', show); window.removeEventListener('message', onMessage); };
  }, [queryClient]);

  if (!allowed) return null;

  // index.html by name: the dev server serves a public file, not a folder, and a bare /plant-app/ falls through to the ERP.
  const src = mode === 'live' ? `${APP_PATH}index.html?site=${encodeURIComponent(site)}` : `${APP_PATH}index.html?mode=sample`;
  const startDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    drag.current = { dx: e.clientX - box.left, dy: e.clientY - box.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moveDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setPos({ x: Math.max(0, Math.min(window.innerWidth - 200, e.clientX - drag.current.dx)),
             y: Math.max(0, Math.min(window.innerHeight - 80, e.clientY - drag.current.dy)) });
  };

  return (
    <>
      {inPlant ? (
        <button type="button" onClick={() => { setMounted(true); setOpen((o) => !o); }} aria-expanded={open}
          aria-label={open ? 'Hide the Plant app' : 'Open the Plant app'} title={open ? 'Hide the Plant app' : 'Open the Plant app'}
          className="fixed right-4 top-1/2 z-40 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-brand text-on-brand shadow-[var(--shadow-overlay)] hover:bg-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
          {open ? <X size={20} aria-hidden /> : <Smartphone size={20} aria-hidden />}
        </button>
      ) : null}

      {mounted ? (
        <div role="dialog" aria-label="Plant app" aria-hidden={!(open && inPlant)}
          className={cx('fixed z-50 flex flex-col overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-overlay)]', open && inPlant ? '' : 'hidden')}
          style={pos ? { left: pos.x, top: pos.y } : { right: 80, top: 64 }}>
          <div onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={() => { drag.current = null; }}
            className="flex cursor-move select-none items-center gap-2 border-b border-line bg-surface-sunken px-2 py-1.5">
            <GripHorizontal size={14} aria-hidden className="text-ink-faint" />
            <div role="radiogroup" aria-label="What the app shows" className="inline-flex overflow-hidden rounded-[var(--radius-control)] border border-line"
              onPointerDown={(e) => e.stopPropagation()}>
              {([['sample', 'Sample day', TestTube2], ['live', 'Live', Radio]] as const).map(([value, label, Icon]) => (
                <button key={value} type="button" role="radio" aria-checked={mode === value} onClick={() => setMode(value)}
                  className={cx('inline-flex items-center gap-1 px-2 py-0.5 text-small', mode === value ? 'bg-brand text-on-brand' : 'bg-surface text-ink-muted hover:text-ink')}>
                  <Icon size={12} aria-hidden /> {label}
                </button>
              ))}
            </div>
            {mode === 'live' ? (
              <select aria-label="Plant" value={site} onChange={(e) => setSite(e.target.value)} onPointerDown={(e) => e.stopPropagation()}
                className="rounded-[var(--radius-control)] border border-line bg-surface px-1 py-0.5 text-small">
                {(plants.data ?? []).map((p) => <option key={p.code} value={p.source_record_id ?? p.code}>{p.code}</option>)}
              </select>
            ) : null}
            <span className="flex-1" />
            <button type="button" onClick={() => setOpen(false)} onPointerDown={(e) => e.stopPropagation()} aria-label="Close" className="text-ink-muted hover:text-ink">
              <X size={16} aria-hidden />
            </button>
          </div>
          {installed.data === false ? (
            <div className="flex w-[380px] flex-col gap-2 p-4 text-small text-ink-muted">
              <p className="text-body text-ink">The Plant app’s web build is not installed yet.</p>
              <p>The plant session builds it; it is copied into apps/erp/public/plant-app/ and appears here.</p>
            </div>
          ) : (
            <iframe key={src} title="Plant app" src={src} className="border-0 bg-paper" style={{ width: 390, height: 780 }}
              allow="camera; clipboard-read; clipboard-write" />
          )}
          <div className={cx('px-2 py-1 text-small', mode === 'live' ? 'bg-refused-ground text-refused' : 'bg-accepted-ground text-accepted')}>
            {mode === 'live' ? 'Live — every entry is a real record in the ERP' : 'Sample day — nothing is saved'}
            {lastSaved ? <span className="text-ink-muted"> · saved {lastSaved}</span> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
